import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardBody, CardHeader, Col, Input, Row } from "reactstrap";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
//
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownOption from "components/Common/DropdownOption";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import useQueryParams from "components/Hooks/useQueryParams";
import { catchAsync, formatNumber } from "helpers/general";
import cmsHubStatistics from "api/cmsHubStatistics";

registerLocale("en", en);
registerLocale("ko", ko);

const TYPE_SELECT_DEFAULT: string = "date";

const KeywordStatistic = () => {
  const { t, i18n } = useTranslation();
  const companies = useSelector((state: any) => state?.Company?.companies);
  const defaultParams = {
    sort_by: TYPE_SELECT_DEFAULT,
    order_by: `DESC`,
    page: 1,
    page_size: 100,
    search: "",
    start_date: moment().subtract(7, "day").format("YYYY-MM-DD"),
    end_date: moment(new Date()).format("Y-MM-DD"),
    company_id: "",
  };
  const [company_id, setCompanyId] = useState<any>();
  const { getQueryParams, clearQueryParams, setQueryParams } =
    useQueryParams(defaultParams);
  const queryParams = getQueryParams();
  const location = useLocation();
  const [rows, setRows] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [dateSearch, setDateSearch] = useState<any[]>([
    moment(queryParams?.start_date || "", "Y-MM-DD").toDate(),
    moment(queryParams?.end_date || "", "Y-MM-DD").toDate(),
  ]);
  const [titleSearch, setTitleSearch] = useState<string>(
    (queryParams?.title as string) || ""
  );
  const [startDate, endDate] = dateSearch;

  const companyOptions = useMemo(() => {
    return (
      companies?.map((item: any) => ({
        value: item?.id,
        label: item?.name,
      })) || []
    );
  }, [companies]);

  const searchData = () => {
    setQueryParams({
      ...queryParams,
      search: titleSearch,
      start_date: startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : moment().subtract(1, "year").format("YYYY-MM-DD"),
      end_date: endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
    });
  };

  const resetData = () => {
    clearQueryParams();
    setDateSearch([moment().subtract(7, "days").toDate(), moment().toDate()]);
    setTitleSearch("");
    setCompanyId(null);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("Date"),
        accessor: "date",
        filterable: false,
        sortable: false,
        thWidth: 130,
        Cell: (cell: any) => (
          <div style={{ minWidth: "100px" }}>
            <span>{cell?.value}</span>
          </div>
        ),
      },
      {
        Header: "PV",
        accessor: "pv",
        filterable: false,
        sortable: false,
        thClass: "text-center",
        thWidth: 130,
        Cell: (cell: any) => (
          <>
            <div className="text-center">{formatNumber(cell?.value)}</div>
          </>
        ),
      },
      {
        Header: "UV",
        accessor: "uv",
        filterable: false,
        sortable: false,
        thClass: "text-center",
        thWidth: 130,
        Cell: (cell: any) => (
          <>
            <div className="text-center">{formatNumber(cell?.value)}</div>
          </>
        ),
      },
      {
        Header: t("cms_statistics.CumulativeUV"),
        accessor: "accumulated_uv",
        filterable: false,
        sortable: false,
        thClass: "text-center",
        thWidth: 170,
        Cell: (cell: any) => (
          <>
            <div className="text-center" style={{ minWidth: "150px" }}>
              {formatNumber(cell?.value)}
            </div>
          </>
        ),
      },
      {
        Header: t("New Keyword  UV"),
        accessor: "new_keyword_uv",
        filterable: false,
        sortable: false,
        thClass: "text-center",
        thWidth: 170,
        description: t("Description New Keyword  UV"),
        Cell: (cell: any) => (
          <>
            <div className="text-center" style={{ minWidth: "150px" }}>
              {formatNumber(cell?.value)}
            </div>
          </>
        ),
      },
      {
        Header: t("New Keyword Statistics"),
        accessor: "new_keywords",
        filterable: false,
        sortable: false,
        thClass: "text-center",
        description: t("Description New Keyword  UV"),
        Cell: (cell: any) => (
          <>
            <div className="text-center" style={{ minWidth: "140px" }}>
              {cell.value ? cell?.value?.join(", ") : "-"}
            </div>
          </>
        ),
      },
    ],
    [i18n?.language]
  );

  const handleChangePage = useCallback((page: any) => {
    setQueryParams({
      ...queryParams,
      page: page + 1,
    });
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  const paging = async () => {
    const [err, result] = await catchAsync(
      cmsHubStatistics.getArticlesKeyword(queryParams),
      setLoading
    );
    if (err) {
      console.log(err);
      return;
    }
    setRows(result?.data);
  };

  useEffect(() => {
    paging();
    if (queryParams?.search) {
      setTitleSearch(queryParams.search as string);
    }
  }, [location.search]);

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card id="leadsList">
            <CardHeader className="border-0">
              <div className="d-flex flex-column flex-md-row g-4 align-items-start align-items-md-center mb-2 mt-2">
                <div className="w-100">
                  <Row className="g-4 align-items-center mb-2 mt-2">
                    <Col sm={12} lg={3} className="mt-2">
                      <LabelWrapper
                        label={`${t("Media")}`}
                        isShow={!!company_id}
                      >
                        <DropdownOption
                          name="type"
                          dataList={companyOptions || []}
                          className="search-filter-category-type"
                          classNamePrefix="name-prefix"
                          initialValue={
                            companyOptions?.find(
                              (item: any) => item.value == company_id
                            ) || {
                              label: t("All"),
                              value: "0",
                            }
                          }
                          onChangeSelect={(event: any) => {
                            console.log(event?.value);
                            // setQueryParams({
                            //   ...queryParams,
                            //   company_id: event?.value,
                            // });
                            setCompanyId(event?.value);
                          }}
                          isHasOptionAll={true}
                          optionAll={{
                            label: t("All"),
                            value: "0",
                          }}
                        />
                      </LabelWrapper>
                    </Col>
                    <Col
                      sm={12}
                      lg={3}
                      className="date-picker-wrapper-custom mt-3 mt-md-2"
                    >
                      <LabelWrapper
                        label={`${t("Date")}`}
                        isShow={!!startDate || !!endDate}
                      >
                        <DatePickerCustom
                          placeholder={`${t("Start Date")} - ${t("End Date")}`}
                          startDate={startDate || null}
                          endDate={endDate || null}
                          isClearable={true}
                          onChangePicker={handleChangePicker}
                        />
                      </LabelWrapper>
                    </Col>
                  </Row>
                  <Row className="g-4 align-items-center mt-0">
                    <Col sm={12} lg={6} className="mt-2">
                      <LabelWrapper
                        label={`${t("Title Post")}`}
                        isShow={!!titleSearch}
                      >
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={`${t("Title Post")}...`}
                          value={titleSearch}
                          onChange={(e) => setTitleSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              searchData();
                            }
                          }}
                        />
                      </LabelWrapper>
                    </Col>
                    <Col
                      sm={12}
                      lg={6}
                      className="hstack gap-1 justify-content-sm-center justify-content-md-start mt-4 mt-md-2"
                    >
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary me-1"
                          onClick={searchData}
                        >
                          <i className="ri-search-line align-bottom me-1"></i>{" "}
                          {t("Button Search")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary fs-14"
                          onClick={resetData}
                        >
                          <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                          {t("Button Reset")}
                        </button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </CardHeader>
            <CardBody className="pt-3">
              <div>
                <TableContainer
                  className="custom-header-css"
                  divClass="table-responsive table-card"
                  tableClass="align-middle mb-0"
                  theadClass="table-light"
                  columns={columns}
                  data={rows || []}
                  customPageSize={rows?.length}
                  customPageIndex={0}
                  totalRecords={rows?.length}
                  customPageCount={1}
                  handleChangePage={handleChangePage}
                  isLoading={loading}
                  isShowPagination={false}
                />
              </div>
              <ToastContainer closeButton={false} limit={1} />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default KeywordStatistic;
